/**
 * Catálogo de proyectos: el proyecto al que pertenece una tarea (por ejemplo `cdtec-backlog`).
 * No todo proyecto es un repo; si lo es, puede guardar un enlace opcional a él.
 */
import { z } from 'zod';
import { enlaceHttpSchema } from './comunes';

export const LARGO_MAX_NOMBRE_PROYECTO = 100;

/**
 * Deja un nombre de proyecto en su forma canónica: Unicode NFC (una «é» escrita como «e» + tilde
 * combinable queda igual que la «é» de un solo carácter, así no se cuelan duplicados que se ven
 * idénticos), sin espacios al inicio ni al final y con los espacios repetidos colapsados en uno.
 * Solo colapsa espacios horizontales (`\p{Zs}`, incluido el espacio duro que pega Google Docs):
 * los tabuladores y saltos de línea internos son caracteres de control y se rechazan después.
 */
export const normalizarNombreProyecto = (nombre: string): string =>
  nombre
    .normalize('NFC')
    .trim()
    .replace(/\p{Zs}+/gu, ' ');

/**
 * Nombre de un proyecto (p. ej. `cdtec-backlog` o «Infraestructura GCP»). Antes era un «slug» de repo;
 * ahora no todo proyecto es un repo, así que se permiten espacios y tildes. Reglas:
 *  - Se normaliza con `normalizarNombreProyecto` (NFC, recorta y colapsa espacios).
 *  - Letras de cualquier alfabeto (con tildes y ñ), números, espacio, punto, guion y guion bajo.
 *    Lista blanca: deja fuera caracteres de control, `/`, emojis y marcas combinables sueltas.
 *  - Empieza con letra o número (como antes: los existentes siguen siendo válidos).
 *  - Máximo 100 caracteres, el mismo largo de antes, para que ningún nombre guardado quede inválido.
 */
export const nombreProyectoSchema = z
  .string()
  .transform(normalizarNombreProyecto)
  .pipe(
    z
      .string()
      .min(1, 'El nombre del proyecto es obligatorio.')
      .max(LARGO_MAX_NOMBRE_PROYECTO, `Máximo ${LARGO_MAX_NOMBRE_PROYECTO} caracteres.`)
      .regex(
        /^[\p{L}\p{N}][\p{L}\p{N} ._-]*$/u,
        'Usa solo letras, números, espacios, punto, guion y guion bajo; empieza con letra o número.',
      ),
  );

/**
 * Clave de unicidad del nombre: la forma canónica en minúsculas. «Infraestructura GCP»,
 * « infraestructura  gcp » e «INFRAESTRUCTURA GCP» dan la misma clave. Las tildes sí cuentan
 * («Diseño» y «Diseno» son distintos). Para los nombres antiguos (solo ASCII sin espacios) da lo
 * mismo que el `toLowerCase()` de antes, así que las claves guardadas siguen calzando.
 */
export const claveNombreProyecto = (nombre: string): string =>
  normalizarNombreProyecto(nombre).toLowerCase();

/**
 * Enlace al repo del proyecto: URL http/https (se rechaza `javascript:`, `data:`, etc. porque se muestra
 * como `<a href>`). Vacío equivale a «sin enlace».
 */
export const enlaceRepoSchema = enlaceHttpSchema;

/** Proyecto tal como lo entrega `GET /api/proyectos`. */
export const proyectoSchema = z.object({
  id: z.number().int(),
  nombre: z.string(),
  /** Un proyecto inactivo no se puede elegir en tareas nuevas, pero las que lo tienen lo conservan. */
  activo: z.boolean(),
  /** Enlace http/https al repo del proyecto; `null` si no tiene. */
  enlaceRepo: z.string().nullable(),
});
export type Proyecto = z.infer<typeof proyectoSchema>;

/** Lo mínimo de un proyecto para mostrarlo dentro de una tarea. */
export const proyectoResumenSchema = z.object({
  id: z.number().int(),
  nombre: z.string(),
  enlaceRepo: z.string().nullable(),
});
export type ProyectoResumen = z.infer<typeof proyectoResumenSchema>;

/** Agregar un proyecto al catálogo (cualquiera con permiso de escritura). El enlace es opcional. */
export const crearProyectoSchema = z.object({
  nombre: nombreProyectoSchema,
  enlaceRepo: enlaceRepoSchema.nullable().optional(),
});
export type CrearProyectoEntrada = z.infer<typeof crearProyectoSchema>;

/**
 * Renombrar, activar/desactivar o cambiar el enlace (solo administradores). `enlaceRepo` vacío o `null`
 * quita el enlace. Sin `.partial()`: Zod 4 y los defaults.
 */
export const actualizarProyectoSchema = z
  .object({
    nombre: nombreProyectoSchema.optional(),
    activo: z.boolean().optional(),
    enlaceRepo: enlaceRepoSchema.nullable().optional(),
  })
  .refine(
    (datos) =>
      datos.nombre !== undefined || datos.activo !== undefined || datos.enlaceRepo !== undefined,
    { message: 'Indica al menos un cambio (nombre, activo o enlaceRepo).' },
  );
export type ActualizarProyectoEntrada = z.infer<typeof actualizarProyectoSchema>;
